import { Button } from '@strata/react-buttons';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { CollapsibleSection } from '@strata/react-collapsible-section';
import { DateInputFormik } from '@strata/react-date-time';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { ErrorToast, SuccessToast } from '@strata/react-toast-message';
import classNames from 'classnames';
import { getIn, useFormikContext } from 'formik';
import PropTypes from 'prop-types';

import { postPriorAuthorization } from 'api/priorAuthManagerAPI';
import { handleError } from 'services/Error';
import {
    AuthFlags,
    formatPriorAuthBody,
    PayerReferralStatuses,
    Sections,
} from 'services/priorAuthorizationHelpers';
import {
    useGetAllSuppliersQuery,
    useGetPlacesOfServiceQuery,
    useLazyGetPrescriberDetailsQuery,
} from 'store/api/referenceApi';

const getId = (fieldId) => `${Sections.AUTH_DETAILS}.${fieldId}`;

export const AuthDetailsSection = ({ setLoadingText, onClear }) => {
    const formik = useFormikContext();

    const { data: allSuppliersData, isFetching: suppliersFetching } = useGetAllSuppliersQuery();
    const { supplierList } = allSuppliersData ?? {};

    const { data: placesOfService, isFetching: placesOfServiceFetching } =
        useGetPlacesOfServiceQuery();

    const [getPrescriberDetails] = useLazyGetPrescriberDetailsQuery();

    const submitAuth = async () => {
        try {
            const { healthPlan } = getIn(formik.values, Sections.PATIENT);
            const { prescriber } = getIn(formik.values, Sections.AUTH_DETAILS);
            const selectedPayer = healthPlan?.payerId;

            const { isError } = await getPrescriberDetails({ prescriberNpi: prescriber });
            if (isError) {
                ErrorToast({ message: 'Prescriber does not exist' });
                return;
            }

            await postPriorAuthorization({
                payerId: selectedPayer,
                authBody: formatPriorAuthBody(formik.values),
            });

            SuccessToast({ message: 'Prior Authorization accepted.' });
            onClear();
        } catch (err) {
            const errorMessage = JSON.parse(err?.message);
            handleError(
                errorMessage?.detail ?? "Can't retrieve Prior Authorization submission results.",
                err,
            );
        }
    };

    return (
        <>
            <TextInputFormik id={getId('caseId')} label="Authorization ID" required />
            <DateInputFormik
                id={getId('serviceStartDate')}
                label="Authorization Start Date"
                onChange={(newValue) =>
                    formik.setFieldValue(
                        getId('serviceStartDate'),
                        newValue === '' ? undefined : newValue,
                    )
                }
            />
            <DateInputFormik
                id={getId('authExpiration')}
                label="Authorization Expiration"
                required
            />
            <TextInputFormik id={getId('prescriber')} label="Prescriber" required />
            <SuggestSingleSelectFormik
                id={getId('placeOfService')}
                label="Place of Service"
                placeholderText="Select Place of Service"
                isLoading={placesOfServiceFetching}
                values={placesOfService}
                required
            />
            <TextInputFormik id={getId('diagnosisCode')} label="Diagnosis Code" required />
            <div className="padding__bottom--medium">
                <CollapsibleSection
                    title="Additional Authorization Detail"
                    renderExpanded={false}
                    content={
                        <>
                            <TextInputFormik id={getId('authReason')} label="Auth Reason" />
                            <SuggestSingleSelectFormik
                                id={getId('payerReferralStatus')}
                                label="Payer Referral Status"
                                placeholderText="Select Payer Referral Status"
                                values={PayerReferralStatuses}
                            />
                            <TextInputFormik id={getId('requestStage')} label="Request Stage" />
                            <TextInputFormik
                                id={getId('specialInstructions')}
                                label="Special Instructions"
                            />
                            <div
                                className={classNames(
                                    'padding__bottom--small',
                                    'font-semi-bold--uppercase',
                                )}
                            >
                                Providers
                            </div>
                            <TextInputFormik
                                id={getId('billingProvider')}
                                label="Billing Provider"
                            />
                            <TextInputFormik
                                id={getId('prescriberPracticeNpi')}
                                label="Prescriber Practice NPI"
                            />
                            <TextInputFormik
                                id={getId('requestingProvider')}
                                label="Requesting Provider"
                            />
                            <TextInputFormik
                                id={getId('servicingFacility')}
                                label="Servicing Facility"
                            />
                            <TextInputFormik
                                id={getId('servicingProvider')}
                                label="Servicing Provider"
                            />
                            <SuggestSingleSelectFormik
                                id={getId('pharmacy')}
                                label="Pharmacy"
                                placeholderText="Select Pharmacy"
                                values={supplierList}
                                isLoading={suppliersFetching}
                            />
                            <div
                                className={classNames(
                                    'padding__bottom--small',
                                    'font-semi-bold--uppercase',
                                )}
                            >
                                Flags
                            </div>
                            <CheckboxListFormik
                                name={getId('flags')}
                                label="Optional Flags"
                                options={Object.values(AuthFlags)}
                            />
                        </>
                    }
                />
            </div>
            <div style={{ display: 'flex', justifyContent: 'right' }}>
                <Button
                    intent={Button.Intent.PRIMARY}
                    disabled={!formik.isValid}
                    label="Submit Prior Authorization"
                    onClick={async () => {
                        setLoadingText('Submitting Prior Authorization');
                        await submitAuth();
                        setLoadingText();
                    }}
                />
            </div>
        </>
    );
};

AuthDetailsSection.propTypes = {
    setLoadingText: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
};
